import React, { useState, useEffect } from 'react';
import { useVoiceWithVAD } from '../../hooks/useVoiceWithVAD';
import { useMobileVoiceFallback } from '../../hooks/useMobileVoiceFallback';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Mic, MicOff, Loader2, Volume2 } from 'lucide-react';
import { useChatInterface } from '@/contexts/ChatInterfaceContext';
import { useIsMobile } from '@/hooks/use-mobile';

interface CleanVoiceInterfaceProps {
  positId: string;
  onEndCall: () => void;
}

export const CleanVoiceInterface: React.FC<CleanVoiceInterfaceProps> = ({ 
  positId, 
  onEndCall 
}) => {
  const { toggleSidebar } = useChatInterface();
  const isMobile = useIsMobile();
  const [useFallback, setUseFallback] = useState(false);

  // Primary voice hook (with VAD)
  const vadVoice = useVoiceWithVAD(positId);
  
  // Fallback voice hook (mobile recording)
  const fallbackVoice = useMobileVoiceFallback(positId);

  // Detect if we need to use fallback
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const hasWebkitSpeechRecognition = 'webkitSpeechRecognition' in window;
    
    // Use fallback if:
    // 1. iOS Safari (limited speech recognition)
    // 2. No speech recognition support
    // 3. Primary method fails
    if ((isIOS && isSafari) || !hasWebkitSpeechRecognition) {
      setUseFallback(true);
    } else if (vadVoice.error && vadVoice.error.includes('Speech recognition')) {
      setUseFallback(true);
    }
  }, [vadVoice.error]);

  // Choose which voice system to use
  const voice = useFallback ? {
    isListening: fallbackVoice.isRecording,
    isProcessing: fallbackVoice.isProcessing,
    isSpeaking: fallbackVoice.isSpeaking,
    currentTranscript: fallbackVoice.transcript,
    lastResponse: fallbackVoice.lastResponse,
    startListening: fallbackVoice.startRecording,
    stopListening: fallbackVoice.stopRecording,
    error: fallbackVoice.error
  } : vadVoice;

  const {
    isListening,
    isProcessing,
    isSpeaking,
    currentTranscript,
    lastResponse,
    startListening,
    stopListening,
    error
  } = voice;

  const handleToggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const getStatusText = () => {
    if (error) return isMobile ? 'Error - Tap to retry' : 'Error - Click to retry';
    if (isProcessing) return 'Processing...';
    if (isSpeaking) return 'Speaking...';
    if (isListening) {
      if (useFallback) {
        return isMobile ? 'Recording... (tap to stop)' : 'Recording... (click to stop)';
      } else {
        return isMobile ? 'Listening... (stops automatically)' : 'Listening... (auto-detects silence)';
      }
    }
    return isMobile ? 'Tap to start talking' : 'Click to start talking';
  };

  const getStatusColor = () => {
    if (error) return 'text-red-500';
    if (isProcessing) return 'text-yellow-500';
    if (isSpeaking) return 'text-blue-500';
    if (isListening) return 'text-green-500';
    return 'text-gray-500';
  };

  const getMicIcon = () => {
    if (isProcessing) return <Loader2 className="h-12 w-12 animate-spin" />;
    if (isSpeaking) return <Volume2 className="h-12 w-12" />;
    if (isListening) return <Mic className="h-12 w-12" />;
    return <MicOff className="h-12 w-12" />;
  };

  const getButtonColor = () => {
    if (error) return 'bg-red-500 hover:bg-red-600';
    if (isProcessing) return 'bg-yellow-500 hover:bg-yellow-600';
    if (isSpeaking) return 'bg-blue-500 hover:bg-blue-600';
    if (isListening) return 'bg-green-500 hover:bg-green-600';
    return 'bg-gray-500 hover:bg-gray-600';
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <Button
          variant="ghost"
          size="icon"
          onClick={onEndCall}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-lg font-semibold">Voice Chat</h1>
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="text-muted-foreground hover:text-foreground"
          >
            <div className="w-5 h-5 flex flex-col justify-between">
              <div className="w-full h-0.5 bg-current"></div>
              <div className="w-full h-0.5 bg-current"></div>
              <div className="w-full h-0.5 bg-current"></div>
            </div>
          </Button>
        )}
      </div>

      {/* Main Voice Interface */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 space-y-8">
        
        {/* Main Voice Button - Mobile Optimized */}
        <div className="relative">
          <Button
            onClick={handleToggleListening}
            disabled={isProcessing || isSpeaking}
            className={`
              ${isMobile ? 'w-40 h-40' : 'w-32 h-32'} 
              rounded-full text-white transition-all duration-300 transform
              ${getButtonColor()}
              ${isListening ? 'scale-110 shadow-lg' : 'hover:scale-105'}
              disabled:opacity-75 disabled:cursor-not-allowed
              touch-manipulation select-none
              min-h-[48px] min-w-[48px]
            `}
            style={{ 
              WebkitTapHighlightColor: 'transparent',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none'
            }}
          >
            <div className={isMobile ? 'scale-125' : ''}>
              {getMicIcon()}
            </div>
          </Button>
          
          {/* Pulsing animation when listening */}
          {isListening && (
            <div className="absolute inset-0 rounded-full bg-green-500/30 animate-ping"></div>
          )}
        </div>

        {/* Status Text */}
        <div className="text-center space-y-2">
          <p className={`text-lg font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </p>
          {error && (
            <p className="text-sm text-muted-foreground text-center px-4">
              {isMobile 
                ? "Allow microphone access in browser settings. Try Chrome for best compatibility."
                : "Make sure microphone access is enabled"}
            </p>
          )}
        </div>

        {/* Transcript Display */}
        {currentTranscript && (
          <div className="w-full max-w-md mx-auto">
            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">You said:</h3>
              <p className="text-sm">{currentTranscript}</p>
            </div>
          </div>
        )}

        {/* Response Display */}
        {lastResponse && (
          <div className="w-full max-w-md mx-auto">
            <div className="bg-primary/10 rounded-lg p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">AI Response:</h3>
              <p className="text-sm">{lastResponse}</p>
            </div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="p-4 border-t bg-muted/30">
        <div className="text-center space-y-1">
          <p className="text-xs text-muted-foreground">
            {isMobile 
              ? "Tap to talk - automatically stops when you finish"
              : "Click to talk - automatically stops when you finish"}
          </p>
          {isMobile && (
            <p className="text-xs text-muted-foreground">
              For best results: Use Chrome browser and allow microphone permissions
            </p>
          )}
        </div>
      </div>
    </div>
  );
};